(function () {
'use strict';
const INTERESTS = Object.freeze([
  "everyday",
  "creativity",
  "cycling",
  "outdoors",
  "travel",
  "music_nightlife",
]);
const STORE_LOCALES = Object.freeze([
  "en",
  "fr",
  "it",
  "de",
  "nl",
  "pl",
  "es",
  "ja",
  "ko",
  "pt-PT",
  "sv",
  "da",
  "no",
]);
const POLICY = Object.freeze({
  activeSeconds: 7,
  pageSeconds: 2,
  quietSeconds: 1,
  overlaySeconds: 1,
  cooldownDays: 14,
  sessionMs: 30 * 60 * 1000,
});
const TRIGGER_DEFAULTS = Object.freeze({ activeSeconds: 7, scrollPercent: 0 });
function triggerPolicy(value) {
  const valid = value && Number.isInteger(value.activeSeconds) && value.activeSeconds >= 3 && value.activeSeconds <= 60
    && Number.isInteger(value.scrollPercent) && value.scrollPercent >= 0 && value.scrollPercent <= 90;
  return valid ? { activeSeconds: value.activeSeconds, scrollPercent: value.scrollPercent } : { ...TRIGGER_DEFAULTS };
}
function locale(value = "en") {
  const tag = String(value).replace("_", "-").toLowerCase();
  if (tag === "pt" || tag.startsWith("pt-")) return "pt-PT";
  const lang = tag.split("-")[0];
  return STORE_LOCALES.includes(lang) ? lang : "en";
}
function unlocalizedPath(value = "/") {
  return (
    String(value)
      .split("?")[0]
      .replace(
        /^\/(?:en|fr|it|de|nl|pl|es|ja|ko|pt|pt-PT|sv|da|no)(?=\/|$)/i,
        "",
      ) || "/"
  );
}
function protectedPage(path, builderPage = false) {
  const p = unlocalizedPath(path);
  if (/^\/(?:cart|checkout|checkouts|account)(?:\/|$)/i.test(p))
    return "cart-checkout-account";
  if (
    builderPage ||
    /^\/pages\/(?:bag-builder|build-your-bag)(?:\/|$)/i.test(p)
  )
    return "builder";
  return null;
}
function route(interests = []) {
  const values = Array.isArray(interests) ? interests : [];
  return {
    email: values.includes("creativity")
      ? "Creative"
      : values.some((v) => v === "outdoors" || v === "travel")
        ? "Days out"
        : "Everyday",
    cycling: values.includes("cycling"),
  };
}
function normalizedInterests(values) {
  if (!Array.isArray(values) || values.some((v) => !INTERESTS.includes(v)))
    throw new TypeError("Invalid interests");
  return INTERESTS.filter((v) => values.includes(v));
}
function profileProperties(values, now = new Date()) {
  const interests = normalizedInterests(values);
  if (!interests.length) throw new TypeError("Select at least one interest");
  return {
    lm_setup_interests: interests,
    lm_setup_interests_updated_at: now.toISOString(),
    lm_setup_quiz_version: "setup_v1",
  };
}
function subscriptionPayload({
  email,
  interests,
  storefrontLocale = "en",
  now = new Date(),
  listId = "LOCAL_REVIEW_ONLY",
}) {
  const address = String(email).trim();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address) || address.length > 254)
    throw new TypeError("Invalid email");
  return {
    data: {
      type: "subscription",
      attributes: {
        custom_source: "Labo Mono setup popup / setup_v1",
        profile: {
          data: {
            type: "profile",
            attributes: {
              email: address,
              locale: locale(storefrontLocale),
              properties: profileProperties(interests, now),
              subscriptions: {
                email: { marketing: { consent: "SUBSCRIBED" } },
              },
            },
          },
        },
      },
      relationships: { list: { data: { type: "list", id: listId } } },
    },
  };
}
// An unanswered compact consent bar yields to the quiz. An active preference
// dialog, unknown consent overlay and shopping controls still take priority.
function blockingOverlay(c) {
  return !!(c.cartOpen || c.otherModal || c.otherForm || c.consentDialog ||
    (c.consentVisible && !c.onlyPendingConsent));
}
function suppression(c, { manual = false, invited = false, ignoreHoldout = false, policy = TRIGGER_DEFAULTS } = {}) {
  // A deliberate footer click is a new request, even after signing up or
  // shopping. Passive triggers and the existing preview API retain their caps.
  if (!(manual && invited)) {
    const page = protectedPage(c.pathname, c.builderPage || c.builderActive);
    if (page) return page;
    if (c.subscriber === true) return "subscriber";
    if (c.submitted) return "submitted";
    if (c.cartKnown !== true) return "cart-unresolved";
    if (c.cartCount > 0 || c.purchaseIntent) return "shopping-session";
  }
  if (blockingOverlay(c))
    return "another-overlay";
  if (!c.pageVisible) return "hidden-tab";
  if (c.editing || c.pointerDown) return "active-interaction";
  if (manual) return null;
  // Consent controls persistence, not whether an eligible visitor can sign up.
  if (c.dismissedUntil > c.now) return "dismissed";
  if (c.shownThisSession) return "session-cap";
  if (c.frequencyCapShown) return "session-cap";
  if (c.frequencyCapAvailable === false && c.storageAvailable === false)
    return "frequency-storage-unavailable";
  if (c.activeSeconds < policy.activeSeconds) return "engagement";
  if (policy.scrollPercent > 0 && c.scrollFraction * 100 < policy.scrollPercent) return "scroll-depth";
  if (c.pageSeconds < POLICY.pageSeconds) return "new-page";
  if (c.quietSeconds < POLICY.quietSeconds) return "interaction-pause";
  if (c.overlaySeconds < POLICY.overlaySeconds) return "overlay-pause";
  if (!ignoreHoldout && c.group === "holdout") return "holdout";
  return null;
}
function sessionState(
  raw,
  now,
  holdoutPercent = 0,
  sample = Math.random(),
) {
  const old = raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {};
  const s = old.session;
  const fresh =
    !s ||
    !Number.isFinite(s.lastActivity) ||
    now - s.lastActivity >= POLICY.sessionMs ||
    s.lastActivity > now + 1000;
  return {
    submitted: old.submitted === true,
    dismissedUntil: Number.isFinite(old.dismissedUntil)
      ? old.dismissedUntil
      : 0,
    session: fresh
      ? {
          startedAt: now,
          lastActivity: now,
          activeSeconds: 0,
          pages: [],
          shown: false,
          purchaseIntent: false,
          eligibleTracked: false,
          group:
            sample * 100 <
            Math.max(0, Math.min(100, Number(holdoutPercent) || 0))
              ? "holdout"
              : "popup",
        }
      : {
          startedAt: Number.isFinite(s.startedAt)
            ? s.startedAt
            : s.lastActivity,
          lastActivity: s.lastActivity,
          activeSeconds: Number.isFinite(s.activeSeconds)
            ? Math.max(0, s.activeSeconds)
            : 0,
          pages: Array.isArray(s.pages)
            ? [...new Set(s.pages.filter((p) => typeof p === "string"))].slice(
                -50,
              )
            : [],
          shown: s.shown === true,
          purchaseIntent: s.purchaseIntent === true,
          eligibleTracked: s.eligibleTracked === true,
          group: s.group === "holdout" ? "holdout" : "popup",
        },
  };
}
// Only frequency/experiment state is persisted. Email and answers stay in memory.
class StateStore {
  constructor({
    storage,
    key = "lm_setup_v1",
    now = Date.now,
    holdoutPercent = 0,
    random = Math.random,
  }) {
    Object.assign(this, { storage, key, now, holdoutPercent, random });
    this.memory = {};
    this.available = !!storage;
  }
  read() {
    let raw = this.memory;
    if (this.available) {
      try {
        raw = JSON.parse(this.storage.getItem(this.key) || "{}");
      } catch {
        this.available = false;
      }
    }
    return sessionState(raw, this.now(), this.holdoutPercent, this.random());
  }
  update(fn) {
    const state = this.read();
    fn(state);
    this.memory = state;
    if (this.available) {
      try {
        this.storage.setItem(this.key, JSON.stringify(state));
      } catch {
        this.available = false;
      }
    }
    return state;
  }
  touch() {
    return this.update((s) => {
      s.session.lastActivity = this.now();
    });
  }
}
// Remember only whether this tab has shown the popup. This is deliberately
// separate from preference storage so an accepted/declined preference choice
// cannot silently change the automatic-opening frequency cap.
class SessionFrequencyCap {
  constructor({ storage, key }) {
    this.storage = storage;
    this.key = key;
    this.available = false;
    this.shown = false;
    if (!storage) return;
    let didWrite = false;
    try {
      this.shown = storage.getItem(key) === "1";
      storage.setItem(key, "1");
      didWrite = true;
      if (!this.shown) storage.removeItem(key);
      this.available = true;
    } catch {
      this.available = false;
      if (didWrite) this.shown = true;
    }
  }
  markShown() {
    this.shown = true;
    if (!this.available) return false;
    try {
      this.storage.setItem(this.key, "1");
      return true;
    } catch {
      this.available = false;
      return false;
    }
  }
}
function frequencyCapKey(preview) {
  return preview ? "lm_setup_review_seen_v2" : "lm_setup_seen_v1";
}
class PreviewService {
  constructor() {
    this.profiles = new Map();
    this.receipts = new Map();
    this.calls = 0;
  }
  async submit(
    payload,
    { requestId, fail = false } = {},
  ) {
    this.calls++;
    if (fail) throw new Error("Simulated unavailable service");
    if (this.receipts.has(requestId)) return this.receipts.get(requestId);
    if (payload.data.relationships.list.data.id !== "LOCAL_REVIEW_ONLY")
      throw new Error("Preview destination required");
    const profile = payload.data.attributes.profile.data.attributes;
    if (!profile.email.endsWith("@example.com"))
      throw new Error("Synthetic addresses only");
    const previous = this.profiles.get(profile.email) || {};
    const result = {
      accepted: true,
      confirmationRequired: false,
      preview: true,
    };
    this.profiles.set(profile.email, {
      ...previous,
      ...structuredClone(profile),
      properties: {
        ...previous.properties,
        ...structuredClone(profile.properties),
      },
    });
    this.receipts.set(requestId, result);
    return result;
  }
}

const COPY = {
  "en": {
    "title": "Find your kind of setup.",
    "footer_body": "Bag and accessory picks inspired by what you’re into.",
    "footer_open": "Find my setup",
    "question": "What would your bag come along for?",
    "helper": "Choose all that apply.",
    "everyday": "Everyday life",
    "creativity": "Creativity",
    "cycling": "Cycling",
    "outdoors": "Outdoors",
    "travel": "Travel",
    "music_nightlife": "Festivals",
    "continue": "Continue",
    "email_title": "Your setup, by email.",
    "email_body": "Bag and accessory picks inspired by your choices, with tips to make them yours.",
    "email_label": "Email address",
    "submit": "Send me my setup",
    "consent": "Unsubscribe anytime.",
    "success_title": "Thanks for signing up.",
    "success_body": "Your signup was received. Look out for your setup by email.",
    "browse": "Continue browsing",
    "back": "Back",
    "close": "Close",
    "privacy": "Privacy policy",
    "selection_error": "Choose at least one option to continue.",
    "email_error": "Enter a valid email address.",
    "submit_error": "That didn’t go through. Your choices are still here. Please try again.",
    "pending": "Sending…",
    "step_one": "Step 1 of 2",
    "step_two": "Step 2 of 2",
    "open": "Find your setup",
    "selection_ready": "Choices selected. Continue is available."
  },
  "fr": {
    "title": "Trouvez la combinaison qui vous correspond.",
    "question": "Pour quelles activités votre sac vous accompagnerait-il ?",
    "helper": "Sélectionnez toutes les options pertinentes.",
    "everyday": "Vie quotidienne",
    "creativity": "Créativité",
    "cycling": "Vélo",
    "outdoors": "outdoor",
    "travel": "Voyages",
    "music_nightlife": "Festivals",
    "continue": "Continuer",
    "email_title": "Votre combinaison, par e-mail.",
    "email_body": "Une sélection de sacs et d’accessoires inspirée de vos choix, avec des conseils pour les adapter à votre usage.",
    "email_label": "Adresse e-mail",
    "submit": "Recevoir ma combinaison",
    "consent": "Vous pouvez vous désabonner à tout moment.",
    "success_title": "Merci pour votre inscription.",
    "success_body": "Votre inscription a bien été reçue. Votre combinaison vous sera envoyée par e-mail.",
    "browse": "Continuer vos achats",
    "back": "Retour",
    "close": "Fermer",
    "privacy": "Politique de confidentialité",
    "selection_error": "Sélectionnez au moins une option pour continuer.",
    "email_error": "Saisissez une adresse e-mail valide.",
    "submit_error": "L’envoi a échoué. Vos choix sont toujours enregistrés. Veuillez réessayer.",
    "pending": "Envoi en cours…",
    "step_one": "Étape 1 sur 2",
    "step_two": "Étape 2 sur 2",
    "open": "Trouver votre combinaison",
    "selection_ready": "Options sélectionnées. Continuer est disponible.",
    "footer_body": "Une sélection de sacs et d’accessoires inspirée de vos centres d’intérêt.",
    "footer_open": "Trouver mon équipement"
  },
  "it": {
    "title": "Trova la tua combinazione.",
    "question": "Dove ti accompagnerà la tua borsa?",
    "helper": "Scegli tutte le opzioni pertinenti.",
    "everyday": "Vita quotidiana",
    "creativity": "Creatività",
    "cycling": "Ciclismo",
    "outdoors": "Vita outdoor",
    "travel": "Viaggi",
    "music_nightlife": "Festival",
    "continue": "Continua",
    "email_title": "La tua combinazione, via email.",
    "email_body": "Una selezione di borse e accessori ispirata alle tue scelte, con consigli per renderli tuoi.",
    "email_label": "Indirizzo email",
    "submit": "Inviami la mia combinazione",
    "consent": "Puoi annullare l’iscrizione in qualsiasi momento.",
    "success_title": "Grazie per l'iscrizione.",
    "success_body": "Abbiamo ricevuto la tua iscrizione. La tua combinazione arriverà via email.",
    "browse": "Continua a navigare",
    "back": "Indietro",
    "close": "Chiudi",
    "privacy": "Informativa sulla privacy",
    "selection_error": "Scegli almeno un'opzione per continuare.",
    "email_error": "Inserisci un indirizzo email valido.",
    "submit_error": "Invio non riuscito. Le tue scelte sono ancora qui. Riprova.",
    "pending": "Invio in corso…",
    "step_one": "Passaggio 1 di 2",
    "step_two": "Passaggio 2 di 2",
    "open": "Trova la tua combinazione",
    "selection_ready": "Opzioni selezionate. Ora puoi continuare.",
    "footer_body": "Scelte di borse e accessori ispirate ai tuoi interessi.",
    "footer_open": "Trovare la mia combinazione"
  },
  "de": {
    "title": "Finde deine Art der Kombination.",
    "question": "Wobei wäre deine Tasche dabei?",
    "helper": "Wähle alles aus, was zutrifft.",
    "everyday": "Alltag",
    "creativity": "Kreativität",
    "cycling": "Radfahren",
    "outdoors": "Outdoor-Leben",
    "travel": "Reisen",
    "music_nightlife": "Festivals",
    "continue": "Weiter",
    "email_title": "Deine Kombination per E-Mail.",
    "email_body": "Passend zu deiner Auswahl erhältst du Vorschläge für Taschen und Zubehör sowie Tipps, um sie zu deinen zu machen.",
    "email_label": "E-Mail-Adresse",
    "submit": "Meine Kombination senden",
    "consent": "Du kannst dich jederzeit abmelden.",
    "success_title": "Danke für deine Anmeldung.",
    "success_body": "Deine Anmeldung ist eingegangen. Deine Kombination erhältst du per E-Mail.",
    "browse": "Weiter stöbern",
    "back": "Zurück",
    "close": "Schließen",
    "privacy": "Datenschutzerklärung",
    "selection_error": "Wähle mindestens eine Option aus, um fortzufahren.",
    "email_error": "Gib eine gültige E-Mail-Adresse ein.",
    "submit_error": "Das hat nicht funktioniert. Deine Auswahl ist noch vorhanden. Bitte versuche es erneut.",
    "pending": "Wird gesendet…",
    "step_one": "Schritt 1 von 2",
    "step_two": "Schritt 2 von 2",
    "open": "Kombination finden",
    "selection_ready": "Auswahl getroffen. „Weiter“ ist verfügbar.",
    "footer_body": "Taschen- und Zubehörauswahl, inspiriert von deinen Interessen.",
    "footer_open": "Mein Setup finden"
  },
  "nl": {
    "title": "Vind je eigen combinatie.",
    "question": "Waarvoor neem je je tas mee?",
    "helper": "Kies alles wat van toepassing is.",
    "everyday": "Dagelijks gebruik",
    "creativity": "Creativiteit",
    "cycling": "Fietsen",
    "outdoors": "outdoor",
    "travel": "Reizen",
    "music_nightlife": "Festivals",
    "continue": "Doorgaan",
    "email_title": "Jouw combinatie, per e-mail.",
    "email_body": "Suggesties voor tassen en accessoires op basis van je keuzes, met tips om ze van jou te maken.",
    "email_label": "E-mailadres",
    "submit": "Mijn combinatie versturen",
    "consent": "Je kunt je op elk moment afmelden.",
    "success_title": "Bedankt voor je inschrijving.",
    "success_body": "We hebben je inschrijving ontvangen. Je combinatie volgt per e-mail.",
    "browse": "Verder winkelen",
    "back": "Terug",
    "close": "Sluiten",
    "privacy": "Privacybeleid",
    "selection_error": "Kies minimaal één optie om door te gaan.",
    "email_error": "Voer een geldig e-mailadres in.",
    "submit_error": "Dat is niet gelukt. Je keuzes staan er nog. Probeer het opnieuw.",
    "pending": "Versturen…",
    "step_one": "Stap 1 van 2",
    "step_two": "Stap 2 van 2",
    "open": "Je combinatie vinden",
    "selection_ready": "Je keuzes zijn geselecteerd. Je kunt doorgaan.",
    "footer_body": "Suggesties voor tassen en accessoires op basis van je interesses.",
    "footer_open": "Mijn combinatie vinden"
  },
  "pl": {
    "title": "Znajdź zestaw dla siebie.",
    "question": "W jakich sytuacjach ma ci towarzyszyć torba?",
    "helper": "Wybierz wszystkie pasujące opcje.",
    "everyday": "Na co dzień",
    "creativity": "Twórczość",
    "cycling": "Rower",
    "outdoors": "Aktywność outdoor",
    "travel": "Podróże",
    "music_nightlife": "Festiwale",
    "continue": "Dalej",
    "email_title": "Twój zestaw w wiadomości e-mail.",
    "email_body": "Propozycje torby i akcesoriów inspirowane twoimi wyborami oraz wskazówki, jak dostosować je do swoich potrzeb.",
    "email_label": "Adres e-mail",
    "submit": "Wyślij mi mój zestaw",
    "consent": "Możesz zrezygnować z subskrypcji w dowolnym momencie.",
    "success_title": "Dzięki za zapisanie się.",
    "success_body": "Otrzymaliśmy twój zapis. Twój zestaw znajdziesz w wiadomości e-mail.",
    "browse": "Przeglądaj dalej",
    "back": "Wstecz",
    "close": "Zamknij",
    "privacy": "Polityka prywatności",
    "selection_error": "Wybierz co najmniej jedną opcję, aby przejść dalej.",
    "email_error": "Wpisz prawidłowy adres e-mail.",
    "submit_error": "Nie udało się wysłać. Twoje wybory pozostały bez zmian. Spróbuj ponownie.",
    "pending": "Wysyłanie…",
    "step_one": "Krok 1 z 2",
    "step_two": "Krok 2 z 2",
    "open": "Znajdź swój zestaw",
    "selection_ready": "Opcje wybrane. Możesz przejść dalej.",
    "footer_body": "Propozycje toreb i akcesoriów inspirowane tym, co lubisz.",
    "footer_open": "Znajdź swój zestaw"
  },
  "es": {
    "title": "Encuentra tu combinación.",
    "question": "¿Para qué usarías tu bolsa?",
    "helper": "Elige todas las opciones que correspondan.",
    "everyday": "Día a día",
    "creativity": "Creatividad",
    "cycling": "Ciclismo",
    "outdoors": "Vida outdoor",
    "travel": "Viajes",
    "music_nightlife": "Festivales",
    "continue": "Continuar",
    "email_title": "Tu combinación, por email.",
    "email_body": "Una selección de bolsas y accesorios basada en tus elecciones, con consejos para hacerlos tuyos.",
    "email_label": "Correo electrónico",
    "submit": "Recibir mi combinación",
    "consent": "Puedes darte de baja en cualquier momento.",
    "success_title": "Gracias por registrarte.",
    "success_body": "Hemos recibido tu registro. Recibirás tu combinación por email.",
    "browse": "Seguir navegando",
    "back": "Volver",
    "close": "Cerrar",
    "privacy": "Política de privacidad",
    "selection_error": "Elige al menos una opción para continuar.",
    "email_error": "Introduce un correo electrónico válido.",
    "submit_error": "No se ha enviado. Tus opciones siguen aquí. Inténtalo de nuevo.",
    "pending": "Enviando…",
    "step_one": "Paso 1 de 2",
    "step_two": "Paso 2 de 2",
    "open": "Encontrar tu combinación",
    "selection_ready": "Opciones seleccionadas. Ya puedes continuar.",
    "footer_body": "Selecciones de bolsas y accesorios inspiradas en tus intereses.",
    "footer_open": "Encontrar mi combinación"
  },
  "ja": {
    "title": "自分らしい組み合わせを探せます。",
    "question": "バッグをどんな場面に連れていきますか。",
    "helper": "当てはまるものをすべて選べます。",
    "everyday": "日常",
    "creativity": "クリエイティブ",
    "cycling": "サイクリング",
    "outdoors": "アウトドア",
    "travel": "旅行",
    "music_nightlife": "音楽フェス",
    "continue": "続ける",
    "email_title": "選んだ組み合わせをメールで届けます。",
    "email_body": "選んだ用途をもとに、バッグとアクセサリーの組み合わせや、自分らしく使うためのヒントを届けます。",
    "email_label": "メールアドレス",
    "submit": "組み合わせを受け取る",
    "consent": "いつでも配信を停止できます。",
    "success_title": "登録ありがとうございます。",
    "success_body": "登録を受け付けました。組み合わせをメールで送ります。",
    "browse": "閲覧を続ける",
    "back": "戻る",
    "close": "閉じる",
    "privacy": "プライバシーポリシー",
    "selection_error": "次へ進むには、少なくとも1つ選んでください。",
    "email_error": "有効なメールアドレスを入力してください。",
    "submit_error": "送信できませんでした。選択内容はそのまま残っています。もう一度お試しください。",
    "pending": "送信中…",
    "step_one": "ステップ 1/2",
    "step_two": "ステップ 2/2",
    "open": "組み合わせを探す",
    "selection_ready": "選択しました。「続ける」が使用できます。",
    "footer_body": "興味に合わせたバッグとアクセサリーの組み合わせを提案します。",
    "footer_open": "自分向けの組み合わせを見つける"
  },
  "ko": {
    "title": "나만의 구성 찾기",
    "question": "가방과 함께할 순간은?",
    "helper": "해당하는 항목을 모두 선택해 주세요.",
    "everyday": "일상",
    "creativity": "창작 활동",
    "cycling": "사이클링",
    "outdoors": "아웃도어",
    "travel": "여행",
    "music_nightlife": "페스티벌",
    "continue": "계속하기",
    "email_title": "이메일로 받아보는 나만의 구성",
    "email_body": "선택한 항목을 바탕으로 가방과 액세서리를 제안하고, 나만의 구성으로 만드는 방법을 알려드려요.",
    "email_label": "이메일 주소",
    "submit": "내 구성 받기",
    "consent": "언제든지 수신 거부할 수 있습니다.",
    "success_title": "구독 신청 감사",
    "success_body": "신청이 접수됐어요. 이메일로 나만의 구성을 보내드릴게요.",
    "browse": "계속 둘러보기",
    "back": "뒤로",
    "close": "닫기",
    "privacy": "개인정보 처리방침",
    "selection_error": "계속하려면 항목을 하나 이상 선택해 주세요.",
    "email_error": "올바른 이메일 주소를 입력해 주세요.",
    "submit_error": "전송되지 않았어요. 선택한 항목은 그대로 남아 있어요. 다시 시도해 주세요.",
    "pending": "전송 중…",
    "step_one": "1/2단계",
    "step_two": "2/2단계",
    "open": "나만의 구성 찾기",
    "selection_ready": "선택이 완료됐어요. 계속하기 버튼을 사용할 수 있어요.",
    "footer_body": "관심사에 맞춰 가방과 액세서리를 추천해 드려요.",
    "footer_open": "나에게 맞는 구성 찾기"
  },
  "pt-PT": {
    "title": "Encontra a tua combinação.",
    "question": "Em que ocasiões levarias a tua bolsa?",
    "helper": "Escolhe todas as opções aplicáveis.",
    "everyday": "Dia a dia",
    "creativity": "Criatividade",
    "cycling": "Ciclismo",
    "outdoors": "Atividades outdoor",
    "travel": "Viagens",
    "music_nightlife": "Festivais",
    "continue": "Continuar",
    "email_title": "A tua combinação, por email.",
    "email_body": "Uma seleção de bolsas e acessórios inspirada nas tuas escolhas, com sugestões para a adaptares a ti.",
    "email_label": "Endereço de email",
    "submit": "Receber a minha combinação",
    "consent": "Podes cancelar a subscrição a qualquer momento.",
    "success_title": "Agradecemos a tua inscrição.",
    "success_body": "Recebemos a tua inscrição. Vais receber a tua combinação por email.",
    "browse": "Continuar a navegar",
    "back": "Voltar",
    "close": "Fechar",
    "privacy": "Política de privacidade",
    "selection_error": "Escolhe pelo menos uma opção para continuar.",
    "email_error": "Introduz um endereço de email válido.",
    "submit_error": "Não foi possível enviar. As tuas escolhas continuam guardadas. Tenta novamente.",
    "pending": "A enviar…",
    "step_one": "Passo 1 de 2",
    "step_two": "Passo 2 de 2",
    "open": "Encontrar a tua combinação",
    "selection_ready": "Opções selecionadas. Podes continuar.",
    "footer_body": "Escolhas de bolsas e acessórios inspiradas no que te interessa.",
    "footer_open": "Encontrar o meu conjunto"
  },
  "sv": {
    "title": "Hitta din typ av kombination.",
    "question": "Vad skulle du ta med väskan till?",
    "helper": "Välj alla som passar.",
    "everyday": "Vardagsliv",
    "creativity": "Kreativitet",
    "cycling": "Cykling",
    "outdoors": "Friluftsliv",
    "travel": "Resor",
    "music_nightlife": "Festivaler",
    "continue": "Fortsätt",
    "email_title": "Din kombination via e-post.",
    "email_body": "Förslag på väskor och tillbehör utifrån dina val, med tips för att anpassa dem efter dig.",
    "email_label": "E-postadress",
    "submit": "Skicka min kombination",
    "consent": "Avsluta prenumerationen när du vill.",
    "success_title": "Tack för din anmälan.",
    "success_body": "Din anmälan har tagits emot. Din kombination skickas via e-post.",
    "browse": "Fortsätt utforska",
    "back": "Tillbaka",
    "close": "Stäng",
    "privacy": "Integritetspolicy",
    "selection_error": "Välj minst ett alternativ för att fortsätta.",
    "email_error": "Ange en giltig e-postadress.",
    "submit_error": "Det gick inte att skicka. Dina val finns kvar. Försök igen.",
    "pending": "Skickar…",
    "step_one": "Steg 1 av 2",
    "step_two": "Steg 2 av 2",
    "open": "Hitta din kombination",
    "selection_ready": "Valen är gjorda. Du kan fortsätta.",
    "footer_body": "Förslag på väskor och tillbehör utifrån dina intressen.",
    "footer_open": "Hitta min uppsättning"
  },
  "da": {
    "title": "Find din type sammensætning.",
    "question": "Hvad skulle din taske med til?",
    "helper": "Vælg alle relevante muligheder.",
    "everyday": "Hverdagen",
    "creativity": "Kreativitet",
    "cycling": "Cykling",
    "outdoors": "outdoor",
    "travel": "Rejser",
    "music_nightlife": "Festivaler",
    "continue": "Fortsæt",
    "email_title": "Din sammensætning på mail.",
    "email_body": "Forslag til tasker og tilbehør baseret på dine valg samt tips til at tilpasse dem til dig.",
    "email_label": "E-mailadresse",
    "submit": "Send mig min sammensætning",
    "consent": "Afmeld dig når som helst.",
    "success_title": "Tak for din tilmelding.",
    "success_body": "Din tilmelding er modtaget. Hold øje med din sammensætning på mail.",
    "browse": "Se videre",
    "back": "Tilbage",
    "close": "Luk",
    "privacy": "Privatlivspolitik",
    "selection_error": "Vælg mindst én mulighed for at fortsætte.",
    "email_error": "Indtast en gyldig e-mailadresse.",
    "submit_error": "Det gik ikke igennem. Dine valg er stadig gemt. Prøv igen.",
    "pending": "Sender…",
    "step_one": "Trin 1 af 2",
    "step_two": "Trin 2 af 2",
    "open": "Find din sammensætning",
    "selection_ready": "Valg foretaget. Fortsæt er nu tilgængelig.",
    "footer_body": "Forslag til tasker og tilbehør ud fra dine interesser.",
    "footer_open": "Find mit setup"
  },
  "no": {
    "title": "Finn din kombinasjon.",
    "question": "Hva skal vesken være med på?",
    "helper": "Velg alle som passer.",
    "everyday": "Hverdagsliv",
    "creativity": "Kreativitet",
    "cycling": "Sykling",
    "outdoors": "Tur",
    "travel": "Reise",
    "music_nightlife": "Festivaler",
    "continue": "Fortsett",
    "email_title": "Din kombinasjon på e-post.",
    "email_body": "Forslag til vesker og tilbehør basert på valgene dine, med tips til hvordan du kan tilpasse dem.",
    "email_label": "E-postadresse",
    "submit": "Send meg kombinasjonen min",
    "consent": "Meld deg av når som helst.",
    "success_title": "Takk for at du meldte deg på.",
    "success_body": "Påmeldingen din er mottatt. Se etter kombinasjonen din på e-post.",
    "browse": "Fortsett å se",
    "back": "Tilbake",
    "close": "Lukk",
    "privacy": "Personvernerklæring",
    "selection_error": "Velg minst ett alternativ for å fortsette.",
    "email_error": "Skriv inn en gyldig e-postadresse.",
    "submit_error": "Det gikk ikke gjennom. Valgene dine er fortsatt her. Prøv igjen.",
    "pending": "Sender…",
    "step_one": "Trinn 1 av 2",
    "step_two": "Trinn 2 av 2",
    "open": "Finn din kombinasjon",
    "selection_ready": "Valg er gjort. Fortsett er tilgjengelig.",
    "footer_body": "Forslag til vesker og tilbehør basert på interessene dine.",
    "footer_open": "Finne det som passer meg"
  }
};

// Public browser endpoint only. This adapter is never used by the review harness.
class KlaviyoSubscription {
  constructor({
    publicSiteId,
    listId,
    preview = true,
    // Call native fetch without binding its receiver to this adapter.
    fetcher = (...args) => fetch(...args),
  }) {
    Object.assign(this, {
      publicSiteId,
      listId,
      preview,
      fetcher,
    });
  }
  async submit(payload) {
    if (
      this.preview ||
      !/^[A-Za-z0-9]{4,12}$/.test(this.publicSiteId || "") ||
      !/^[A-Za-z0-9]{4,12}$/.test(this.listId || "") ||
      payload.data.relationships.list.data.id !== this.listId
    ) {
      throw new Error("Live subscription is not configured");
    }
    const response = await this.fetcher(
      "https://a.klaviyo.com/client/subscriptions/?company_id=" +
        encodeURIComponent(this.publicSiteId),
      {
        method: "POST",
        credentials: "omit",
        headers: {
          "Content-Type": "application/vnd.api+json",
          accept: "application/vnd.api+json",
          revision: "2026-07-15",
        },
        body: JSON.stringify(payload),
        signal: AbortSignal.timeout(15000),
      },
    );
    if (response.status !== 202) {
      const error = new Error("Subscription request was not accepted");
      error.kind = response.status === 429 ? "rate_limited" : "rejected";
      throw error;
    }
    return {
      accepted: true,
      confirmationRequired: false,
      preview: false,
    };
  }
}

const FUNNEL_EVENTS = new Set([
  "eligible",
  "opened",
  "quiz_completed",
  "email_viewed",
  "email_submitted",
  "submit_attempted",
  "accepted",
  "error",
  "dismissed",
  "interrupted",
]);
const ONCE_PER_OPEN = new Set([
  "quiz_completed",
  "email_viewed",
  "email_submitted",
  "accepted",
  "dismissed",
  "interrupted",
]);
const enumValue = (value, allowed, fallback) =>
  allowed.includes(value) ? value : fallback;

// Only low-cardinality, explicitly allowed fields can reach the analytics sink.
// No email, profile ID, answers, request ID, URL or error text is forwarded.
function measurementPayload(context = {}, details = {}) {
  const payload = {
    form_id: "lm_setup_v1",
    form_provider: "in_house",
    analytics_version: "funnel_v2",
    quiz_version: "setup_v1",
    storefront_locale: locale(context.locale),
    experiment_group: enumValue(context.group, ["popup", "holdout"], "popup"),
    device_group: enumValue(context.device, ["mobile", "desktop"], "desktop"),
    opening_method: enumValue(context.method, ["automatic", "manual"], "none"),
  };
  if (
    Number.isInteger(details.interest_count) &&
    details.interest_count >= 0 &&
    details.interest_count <= 6
  )
    payload.interest_count = details.interest_count;
  if ([1, 2, 3].includes(details.step)) payload.step = details.step;
  if (Number.isInteger(details.attempt_number) && details.attempt_number > 0)
    payload.attempt_number = Math.min(details.attempt_number, 100);
  if (typeof details.is_retry === "boolean")
    payload.is_retry = details.is_retry;
  if (typeof details.confirmation_required === "boolean")
    payload.confirmation_required = details.confirmation_required;
  if (details.error_kind !== undefined)
    payload.error_kind = enumValue(
      details.error_kind,
      ["unavailable", "rejected", "rate_limited"],
      "unavailable",
    );
  if (details.interruption_reason !== undefined)
    payload.interruption_reason = enumValue(
      details.interruption_reason,
      ["shopping", "overlay", "protected_page"],
      "overlay",
    );
  return payload;
}

class PopupMeasurement {
  constructor({
    context,
    consent,
    preview = true,
    measurementId,
    send,
    observe,
  }) {
    Object.assign(this, {
      context,
      consent,
      preview,
      measurementId,
      send,
      observe,
    });
    this.attempts = new WeakMap();
  }
  opened(method, step) {
    this.journey = { ...this.context(), method, seen: new Set() };
    this.event("opened", { step });
    // Explicit reopening preserves the UI's email step; do not invent a quiz completion.
    if (step === 2) this.event("email_viewed", { step: 2 });
    return this.journey;
  }
  submission(attempt, interestCount) {
    const attemptNumber = (this.attempts.get(attempt) || 0) + 1;
    this.attempts.set(attempt, attemptNumber);
    // Capture the opening context so a delayed result cannot inherit a later reopen.
    const journey = { ...this.journey, locale: this.context().locale };
    const details = {
      interest_count: interestCount,
      step: 2,
      attempt_number: attemptNumber,
      is_retry: attemptNumber > 1,
    };
    this.event("email_submitted", details, journey);
    this.event("submit_attempted", details, journey);
    return { journey, details };
  }
  event(name, details = {}, journey = this.journey) {
    if (!FUNNEL_EVENTS.has(name)) return false;
    if (name !== "eligible" && !journey) return false;
    if (ONCE_PER_OPEN.has(name)) {
      if (journey.seen.has(name)) return false;
      journey.seen.add(name);
    }
    const context =
      name === "eligible"
        ? this.context()
        : { ...journey, locale: this.context().locale };
    // A submission snapshot retains its locale even when the UI later changes.
    if (
      journey &&
      ["email_submitted", "submit_attempted", "accepted", "error"].includes(
        name,
      )
    )
      context.locale = journey.locale;
    const payload = measurementPayload(context, details);
    try {
      this.observe?.(name, payload);
    } catch {
      /* Analytics must never block signup. */
    }
    try {
      if (
        this.preview ||
        !this.consent() ||
        !/^G-[A-Z0-9]+$/.test(this.measurementId || "")
      )
        return false;
      this.send("setup_signup_" + name, {
        ...payload,
        send_to: this.measurementId,
      });
      return true;
    } catch {
      return false;
    }
  }
}

const ENDPOINT = "https://labomono.vercel.app/api/client-beacon";
const MAX_EVENTS = 100;
const MAX_LIFETIME_MS = 24 * 60 * 60 * 1000;
const MAX_TRANSPORT_ATTEMPTS = 3;
const OPENINGS = new Set(["automatic", "manual"]);
const DEVICES = new Set(["mobile", "desktop"]);

const uuid = () => {
  try {
    if (typeof globalThis.crypto?.randomUUID === "function")
      return globalThis.crypto.randomUUID();
    if (typeof globalThis.crypto?.getRandomValues === "function") {
      const bytes = globalThis.crypto.getRandomValues(new Uint8Array(16));
      bytes[6] = (bytes[6] & 0x0f) | 0x40;
      bytes[8] = (bytes[8] & 0x3f) | 0x80;
      return [...bytes].map((value) => value.toString(16).padStart(2, "0"))
        .join("")
        .replace(/^(.{8})(.{4})(.{4})(.{4})(.{12})$/, "$1-$2-$3-$4-$5");
    }
  } catch {}
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const value = Math.random() * 16 | 0;
    return (char === "x" ? value : (value & 0x3) | 0x8).toString(16);
  });
};

const canonicalInterests = (values) =>
  normalizedInterests(Array.isArray(values) ? values : []);

const stateKey = (state) => JSON.stringify([
  state.opening,
  state.device,
  state.locale,
  state.interests,
  state.selectedEver,
  state.continued,
  state.submitted,
  state.accepted,
]);

/**
 * Temporary pseudonymous popup progress. State and transport queues are held
 * only in this document. The server keeps one latest state per attempt and
 * derives daily counters, so intermediate pending snapshots may be coalesced.
 */
class PopupProgress {
  constructor({
    enabled = false,
    mode = "consented",
    consent,
    preview = true,
    context = () => ({}),
    send,
    endpoint = ENDPOINT,
    observe,
    randomUUID,
    now = Date.now,
    retryDelay = 250,
    setTimeoutFn = globalThis.setTimeout,
    clearTimeoutFn = globalThis.clearTimeout,
    maxTransportAttempts = MAX_TRANSPORT_ATTEMPTS,
  } = {}) {
    this.mode = mode;
    this.enabled = enabled === true && ["consented", "aggregate-v2"].includes(mode);
    this.consent = typeof consent === "function" ? consent : () => false;
    this.preview = preview === true;
    this.context = context;
    this.send = send || ((url, options) => fetch(url, options));
    this.endpoint = endpoint;
    this.observe = observe;
    this.randomUUID = randomUUID || uuid;
    this.now = now;
    this.retryDelay = Math.max(0, Number(retryDelay) || 0);
    this.setTimeout = setTimeoutFn;
    this.clearTimeout = clearTimeoutFn;
    this.maxTransportAttempts = Math.max(1, Math.floor(Number(maxTransportAttempts) || MAX_TRANSPORT_ATTEMPTS));
    this.attemptId = null;
    this.startedAt = null;
    this.expired = false;
    this.opening = "manual";
    this.metadata = null;
    this.visible = false;
    this.state = {
      interests: [],
      selectedEver: false,
      continued: false,
      submitted: false,
      accepted: false,
    };
    this.sequence = 0;
    this.lastKey = null;
    this.lastConsent = false;
    this.frozen = false;
    this.transportGeneration = 0;
    this.inFlight = null;
    this.pending = null;
    this.retryTimer = null;
    this.transportBlocked = false;
  }

  currentContext() {
    const value = this.context() || {};
    return {
      opening: OPENINGS.has(value.opening) ? value.opening : this.opening || "manual",
      device: DEVICES.has(value.device) ? value.device : "desktop",
      locale: locale(value.locale),
    };
  }

  snapshot(overrides = {}) {
    const context = this.metadata || this.currentContext();
    const flag = (name) =>
      Object.hasOwn(overrides, name) ? overrides[name] === true : this.state[name];
    return {
      opening: context.opening,
      device: context.device,
      locale: context.locale,
      interests: canonicalInterests(overrides.interests ?? this.state.interests),
      selectedEver: flag("selectedEver"),
      continued: flag("continued"),
      submitted: flag("submitted"),
      accepted: flag("accepted"),
    };
  }

  clearRetryTimer() {
    if (this.retryTimer !== null) {
      try { this.clearTimeout(this.retryTimer); } catch {}
      this.retryTimer = null;
    }
  }

  invalidateTransport() {
    this.clearRetryTimer();
    this.pending = null;
    this.transportBlocked = true;
    this.transportGeneration += 1;
  }

  resetMeasuredJourney() {
    this.clearRetryTimer();
    this.pending = null;
    this.inFlight = null;
    this.transportGeneration += 1;
    this.transportBlocked = false;
    this.attemptId = this.randomUUID();
    this.startedAt = this.now();
    this.expired = false;
    this.sequence = 0;
    this.lastKey = null;
    this.metadata = this.currentContext();
    this.frozen = false;
    this.state = {
      interests: [],
      selectedEver: false,
      continued: false,
      submitted: false,
      accepted: false,
    };
  }

  expiredAttempt() {
    if (!this.attemptId || !Number.isFinite(this.startedAt)) return false;
    if (this.now() - this.startedAt < MAX_LIFETIME_MS) return false;
    this.expired = true;
    this.invalidateTransport();
    return true;
  }

  ensureConsentTransition() {
    if (this.expiredAttempt()) return false;
    const allowed = this.consent() === true;
    if (!allowed) {
      if (this.lastConsent) this.invalidateTransport();
      this.state = {
        interests: [],
        selectedEver: false,
        continued: false,
        submitted: false,
        accepted: false,
      };
      this.lastConsent = false;
      return false;
    }
    if (!this.lastConsent && !this.frozen && this.visible) {
      // Consent granted while the popup is visible starts a fresh measured
      // attempt. Only a new opening baseline is emitted; refused actions are
      // never replayed into the newly permitted attempt.
      this.resetMeasuredJourney();
      this.emit(true, {
        interests: [],
        selectedEver: false,
        continued: false,
        submitted: false,
        accepted: false,
      });
    }
    this.lastConsent = true;
    return true;
  }

  open(opening = "manual", interests = this.state.interests) {
    if (this.frozen || this.expired) return false;
    this.opening = OPENINGS.has(opening) ? opening : "manual";
    this.visible = true;
    const allowed = this.ensureConsentTransition();
    if (!allowed || this.preview) return false;
    if (!this.attemptId) this.resetMeasuredJourney();
    if (this.expiredAttempt()) return false;
    this.state.interests = canonicalInterests(interests);
    if (this.state.interests.length) this.state.selectedEver = true;
    if (this.sequence === 0) {
      this.emit(true, {
        interests: [],
        selectedEver: false,
        continued: false,
        submitted: false,
        accepted: false,
      });
    }
    return this.emit(true);
  }

  selection(interests) {
    if (this.frozen || this.expiredAttempt()) return false;
    if (!this.ensureConsentTransition()) return false;
    this.state.interests = canonicalInterests(interests);
    if (this.state.interests.length) this.state.selectedEver = true;
    return this.visible && this.emit();
  }

  continue(interests = this.state.interests) {
    if (this.frozen || this.expiredAttempt()) return false;
    if (!this.ensureConsentTransition()) return false;
    this.state.interests = canonicalInterests(interests);
    if (this.state.interests.length) this.state.selectedEver = true;
    this.state.continued = true;
    return this.visible && this.emit();
  }

  submitted(interests = this.state.interests) {
    if (this.frozen || this.expiredAttempt()) return null;
    if (!this.ensureConsentTransition()) return null;
    this.state.interests = canonicalInterests(interests);
    if (this.state.interests.length) this.state.selectedEver = true;
    // If consent was granted while the UI was already on the email step, the
    // permitted submission is the first measurable observation of Continue.
    // A selection alone remains a pre-Continue state and does not set this.
    if (this.state.interests.length) this.state.continued = true;
    this.state.submitted = true;
    if (this.visible) this.emit();
    return this.attemptId;
  }

  accepted(attemptId = this.attemptId) {
    if (this.frozen || this.expiredAttempt()) return false;
    // A delayed provider result must belong to the same attempt. Consent
    // transitions can replace an attempt while that result is pending.
    if (!this.ensureConsentTransition() || attemptId !== this.attemptId) return false;
    if (!this.state.submitted) return false;
    this.state.accepted = true;
    const sent = this.emit(true);
    this.frozen = true;
    return sent;
  }

  close() {
    this.visible = false;
  }

  consentChanged() {
    return this.ensureConsentTransition();
  }

  isSuccessfulResponse(response) {
    return !response || response.ok !== false;
  }

  finishTransport(record, ok) {
    if (record.generation !== this.transportGeneration || record.attemptId !== this.attemptId)
      return;
    if (this.inFlight !== record) return;
    if (!ok) {
      if (record.attempts < this.maxTransportAttempts && !this.expiredAttempt() && this.consent() === true) {
        const delay = this.retryDelay * Math.pow(2, record.attempts - 1);
        this.retryTimer = this.setTimeout(() => {
          this.retryTimer = null;
          if (this.inFlight === record) this.startTransport(record);
        }, delay);
        return;
      }
      this.inFlight = null;
      this.pending = null;
      this.transportBlocked = true;
      return;
    }
    this.inFlight = null;
    if (this.pending && !this.transportBlocked && !this.expiredAttempt() && this.consent() === true) {
      const next = this.pending;
      this.pending = null;
      this.startTransport(next);
    }
  }

  startTransport(record) {
    if (record.generation !== this.transportGeneration || record.attemptId !== this.attemptId)
      return;
    if (this.transportBlocked || this.expiredAttempt() || this.consent() !== true) return;
    this.inFlight = record;
    record.attempts += 1;
    let result;
    try {
      result = this.send(this.endpoint, {
        method: "POST",
        headers: { "content-type": "text/plain;charset=UTF-8" },
        body: JSON.stringify(record.payload),
        keepalive: true,
        credentials: "omit",
        referrerPolicy: "no-referrer",
      });
    } catch {
      this.finishTransport(record, false);
      return;
    }
    Promise.resolve(result).then(
      (response) => this.finishTransport(record, this.isSuccessfulResponse(response)),
      () => this.finishTransport(record, false),
    );
  }

  enqueue(payload) {
    if (this.transportBlocked || this.consent() !== true) return;
    const record = {
      payload,
      attemptId: this.attemptId,
      generation: this.transportGeneration,
      attempts: 0,
    };
    if (this.inFlight) {
      // The aggregate endpoint only needs the latest state after the
      // sequence-1 opening snapshot has been accepted.
      this.pending = record;
      return;
    }
    this.startTransport(record);
  }

  emit(allowHidden = false, overrides = {}) {
    if (!this.enabled || this.preview || this.frozen || this.expiredAttempt())
      return false;
    if (this.consent() !== true) return false;
    if (!allowHidden && !this.visible) return false;
    if (!this.attemptId) this.resetMeasuredJourney();
    if (this.sequence >= MAX_EVENTS) return false;
    const state = this.snapshot(overrides);
    const key = stateKey(state);
    if (key === this.lastKey) return false;
    const payload = {
      type: "setup_popup_progress",
      measurementVersion: 2,
      attemptId: this.attemptId,
      sequence: this.sequence + 1,
      opening: state.opening,
      device: state.device,
      locale: state.locale,
      interests: state.interests,
      selectedEver: state.selectedEver,
      continued: state.continued,
      submitted: state.submitted,
      accepted: state.accepted,
    };
    this.sequence = payload.sequence;
    this.lastKey = key;
    try {
      this.observe?.(payload);
    } catch {}
    this.enqueue(payload);
    return true;
  }
}

{ ENDPOINT, MAX_EVENTS, MAX_LIFETIME_MS, MAX_TRANSPORT_ATTEMPTS };

const escapeHtml = (value) =>
  String(value ?? "").replace(
    /[&<>"']/g,
    (c) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      })[c],
  );
const ICONS = {
  everyday: '<path d="M4 10 12 3l8 7v10H4zM9 20v-7h6v7"/>',
  creativity:
    '<rect x="3" y="7" width="18" height="13" rx="2"/><path d="m7 7 2-3h6l2 3"/><circle cx="12" cy="13" r="3"/>',
  cycling:
    '<circle cx="5" cy="16" r="4"/><circle cx="19" cy="16" r="4"/><path d="m5 16 5-9 5 9H5m5-9h7l2 9M8 4h4m4 0h3l1 3"/>',
  outdoors: '<path d="m2 20 8-15 4 7 3-5 5 13H2Zm5-9 3 2 3-2"/>',
  travel:
    '<rect x="5" y="6" width="14" height="14" rx="2"/><path d="M9 6V3h6v3M8 20v2m8-2v2M9 9v8m6-8v8"/>',
  music_nightlife:
    '<path d="M3 6h18v4a2 2 0 0 0 0 4v4H3v-4a2 2 0 0 0 0-4V6Z"/><path d="M15 6v2m0 3v2m0 3v2"/>',
};
const icon = (value) =>
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  ICONS[value] +
  "</svg>";
let uiNumber = 0;

class PopupUI {
  constructor(root, config, {
    onDismiss,
    onSubmit,
    onEvent,
    onOpenRequest,
    onInterestChange,
    onContinue,
  }) {
    Object.assign(this, {
      root,
      config,
      onDismiss,
      onSubmit,
      onEvent,
      onOpenRequest,
      onInterestChange,
      onContinue,
    });
    this.id = "lm-setup-" + ++uiNumber;
    this.selected = new Set();
    this.step = 1;
    this.email = "";
    this.busy = false;
    this.mediaKey = "";
    this.imagesLoadedKey = "";
    this.imagesPrepared = false;
    this.lang = locale(config.locale);
    this.signalController = new AbortController();
    root.classList.add("lm-setup");
    root.innerHTML =
      (config.preview === true || config.designMode === true
        ? '<div class="lm-setup-invitation" hidden><button type="button" class="lm-setup-invite"></button></div>'
        : "") +
      '<dialog class="lm-setup-dialog" aria-modal="true" aria-labelledby="' +
      this.id +
      '-title">' +
      '<button type="button" class="lm-setup-close"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18"/></svg></button>' +
      '<div class="lm-setup-grid"><div class="lm-setup-photo" aria-hidden="true"><picture><source media="(max-width: 699px)"><img alt="" width="800" height="1100"></picture><div class="lm-setup-photo-brand"><img data-lm-logo alt="" width="144" height="40"></div></div>' +
      '<div class="lm-setup-panel"><div class="lm-setup-top"><button type="button" class="lm-setup-back"><span aria-hidden="true">←</span><span></span></button><span class="lm-setup-counter"></span></div>' +
      '<div class="lm-setup-progress" aria-hidden="true"><i></i><i></i></div><div class="lm-setup-stage"></div></div></div></dialog>';
    this.dialog = root.querySelector("dialog");
    this.stage = root.querySelector(".lm-setup-stage");
    this.invite = root.querySelector(".lm-setup-invite");
    this.setMedia(config);
    const signal = this.signalController.signal;
    const fitViewport = () => this.fitViewport();
    window.visualViewport?.addEventListener("resize", fitViewport, { signal });
    window.visualViewport?.addEventListener("scroll", fitViewport, { signal });
    window.addEventListener("resize", fitViewport, { signal });
    root
      .querySelector(".lm-setup-close")
      .addEventListener("click", () => this.onDismiss(true), { signal });
    root.querySelector(".lm-setup-back").addEventListener(
      "click",
      () => {
        if (!this.busy) this.go(1, -1);
      },
      { signal },
    );
    this.invite?.addEventListener("click", () => this.onOpenRequest(), {
      signal,
    });
    this.dialog.addEventListener(
      "cancel",
      (event) => {
        event.preventDefault();
        this.onDismiss(true);
      },
      { signal },
    );
    this.dialog.addEventListener(
      "click",
      (event) => {
        if (
          event.target !== this.dialog ||
          !matchMedia("(hover: hover) and (pointer: fine)").matches
        )
          return;
        const b = this.dialog.getBoundingClientRect();
        if (
          event.clientX < b.left ||
          event.clientX > b.right ||
          event.clientY < b.top ||
          event.clientY > b.bottom
        )
          this.onDismiss(true);
      },
      { signal },
    );
    // Keep the tab cycle inside the dialog, including browsers that otherwise
    // hand focus to browser chrome after the last control.
    this.dialog.addEventListener(
      "keydown",
      (event) => {
        if (event.key !== "Tab") return;
        const controls = [
          ...this.dialog.querySelectorAll(
            'button, input, a[href], [tabindex="0"]',
          ),
        ].filter(
          (element) =>
            !element.disabled &&
            element.getClientRects().length &&
            getComputedStyle(element).visibility !== "hidden",
        );
        const first = controls[0],
          last = controls.at(-1);
        if (
          event.shiftKey &&
          (document.activeElement === first ||
            !controls.includes(document.activeElement))
        ) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      },
      { signal },
    );
    this.stage.addEventListener(
      "change",
      (event) => {
        if (!event.target.matches('input[name="interest"]')) return;
        if (event.target.checked) this.selected.add(event.target.value);
        else this.selected.delete(event.target.value);
        this.pendingAttempt = null;
        this.onInterestChange?.([...this.selected]);
        this.syncSelection();
      },
      { signal },
    );
    this.stage.addEventListener(
      "input",
      (event) => {
        if (event.target.name !== "email") return;
        this.email = event.target.value;
        this.pendingAttempt = null;
        event.target.removeAttribute("aria-invalid");
        this.showError("");
      },
      { signal },
    );
    this.stage.addEventListener(
      "submit",
      (event) => {
        event.preventDefault();
        if (this.busy) return;
        if (this.step === 1) {
          if (!this.selected.size) return;
          this.onContinue?.([...this.selected]);
          this.onEvent("quiz_completed", {
            interest_count: this.selected.size,
          });
          this.go(2);
          this.onEvent("email_viewed", { step: 2 });
        } else if (this.step === 2) this.submit();
      },
      { signal },
    );
    this.stage.addEventListener(
      "click",
      (event) => {
        if (event.target.closest("[data-lm-browse]")) this.onDismiss(false);
      },
      { signal },
    );
    root
      .querySelector(".lm-setup-photo img")
      .addEventListener(
        "error",
        () => this.dialog.classList.add("lm-setup-image-failed"),
        { signal },
      );
    root.querySelector(".lm-setup-photo img").addEventListener(
      "load",
      () => {
        if (this.imagesLoadedKey === this.mediaKey)
          this.dialog.classList.remove("lm-setup-image-failed");
      },
      { signal },
    );
    this.render();
  }
  reducedMotion() {
    return (
      !!(this.config.preview && this.config.reducedMotion) ||
      matchMedia("(prefers-reduced-motion: reduce)").matches
    );
  }
  fitViewport() {
    const viewport = window.visualViewport;
    this.root.style.setProperty(
      "--lm-viewport-height",
      (viewport?.height || innerHeight) + "px",
    );
    this.root.style.setProperty(
      "--lm-viewport-top",
      (viewport?.offsetTop || 0) + "px",
    );
  }
  copy() {
    return {
      ...COPY.en,
      ...(COPY[this.lang] || {}),
      ...(this.lang === locale(this.config.locale)
        ? this.config.copy || {}
        : {}),
    };
  }
  setLocale(value) {
    const next = locale(value);
    if (next === this.lang) return;
    this.lang = next;
    if (!this.busy) this.pendingAttempt = null;
    this.render();
  }
  setMedia(config) {
    const safeImage = (value) => {
      if (!value) return "";
      try {
        const u = new URL(value, location.href);
        return ["https:", "http:", "blob:"].includes(u.protocol) ? u.href : "";
      } catch {
        return "";
      }
    };
    const desktopImage = safeImage(config.image || "");
    const mobileImage = safeImage(config.mobileImage || config.image || "");
    const mediaKey = JSON.stringify([desktopImage, mobileImage]);
    const mediaChanged = mediaKey !== this.mediaKey;
    this.desktopImage = desktopImage;
    this.mobileImage = mobileImage;
    this.mediaKey = mediaKey;
    if (mediaChanged) {
      this.imagesLoadedKey = "";
    }
    const crop = (value) =>
      /^\d+(?:\.\d+)?%\s+\d+(?:\.\d+)?%$/.test(value || "") ? value : "50% 50%";
    this.root.style.setProperty(
      "--lm-photo-position",
      crop(config.imagePosition),
    );
    this.root.style.setProperty(
      "--lm-mobile-photo-position",
      crop(config.mobilePosition),
    );
    if (this.dialog.open || (mediaChanged && this.imagesPrepared))
      this.loadImages({ warm: !this.dialog.open });
  }
  loadImages({ warm = false } = {}) {
    const logo = this.root.querySelector("[data-lm-logo]");
    const source = this.root.querySelector("source");
    const img = this.root.querySelector(".lm-setup-photo img");
    const logoUrl =
      "https://d3k81ch9hvuctc.cloudfront.net/company/Un3UGa/images/4a2122d0-0a36-44bd-8387-bd960573ab68.png";
    if (logo.src !== logoUrl) logo.src = logoUrl;
    img.loading = "eager";
    img.decoding = "async";
    img.fetchPriority = warm ? "low" : "auto";
    if (this.imagesLoadedKey !== this.mediaKey) {
      this.dialog.classList.toggle("lm-setup-no-image", !this.desktopImage);
      this.dialog.classList.remove("lm-setup-image-failed");
      if (this.mobileImage) source.srcset = this.mobileImage;
      else source.removeAttribute("srcset");
      if (this.desktopImage) img.src = this.desktopImage;
      else img.removeAttribute("src");
      this.imagesLoadedKey = this.mediaKey;
    }
    this.imagesPrepared = true;
  }
  prepareImages() {
    if (!this.dialog.open) this.loadImages({ warm: true });
  }
  syncSelection() {
    const next = this.stage.querySelector("[data-lm-next]");
    if (next) next.disabled = this.selected.size === 0;
    this.stage.querySelectorAll('input[name="interest"]').forEach((input) => {
      input.checked = this.selected.has(input.value);
      input
        .closest("label")
        .classList.toggle("lm-setup-selected", input.checked);
    });
    const status = this.stage.querySelector("[data-lm-selection-status]");
    if (status)
      status.textContent = this.selected.size
        ? this.copy().selection_ready
        : "";
  }
  render() {
    const currentEmail = this.stage.querySelector('input[name="email"]');
    if (currentEmail) this.email = currentEmail.value;
    const c = this.copy(),
      e = escapeHtml;
    this.dialog.lang = this.lang;
    this.dialog.dataset.step = String(this.step);
    if (this.invite) this.invite.textContent = c.open;
    this.root
      .querySelector(".lm-setup-close")
      .setAttribute("aria-label", c.close);
    const back = this.root.querySelector(".lm-setup-back");
    back.querySelector("span:last-child").textContent = c.back;
    back.hidden = this.step !== 2;
    back.disabled = this.busy;
    this.root.querySelector(".lm-setup-counter").textContent =
      this.step === 1 ? c.step_one : this.step === 2 ? c.step_two : "";
    const heading = (text) =>
      '<h2 id="' +
      this.id +
      '-title" class="lm-setup-title" tabindex="-1">' +
      e(text) +
      "</h2>";
    const error =
      '<p class="lm-setup-error" id="' + this.id + '-error" role="alert"></p>';
    if (this.step === 1) {
      this.stage.innerHTML =
        '<form class="lm-setup-form"><div class="lm-setup-main">' +
        heading(c.title) +
        '<fieldset><legend class="lm-setup-question">' +
        e(c.question) +
        '</legend><p class="lm-setup-helper">' +
        e(c.helper) +
        '</p><div class="lm-setup-options">' +
        INTERESTS.map(
          (value) =>
            '<label class="lm-setup-option"><input type="checkbox" name="interest" value="' +
            value +
            '"><span class="lm-setup-choice-icon">' +
            icon(value) +
            '</span><span class="lm-setup-choice-label">' +
            e(c[value]) +
            '</span><span class="lm-setup-check" aria-hidden="true"></span></label>',
        ).join("") +
        '</div></fieldset><p class="lm-setup-sr" data-lm-selection-status aria-live="polite" aria-atomic="true"></p></div><div class="lm-setup-footer"><button class="lm-setup-primary" type="submit" data-lm-next disabled>' +
        e(c.continue) +
        '<span aria-hidden="true">→</span></button></div></form>';
      this.syncSelection();
    } else if (this.step === 2) {
      this.stage.innerHTML =
        '<form class="lm-setup-form" novalidate><div class="lm-setup-main">' +
        heading(c.email_title) +
        '<p class="lm-setup-body">' +
        e(c.email_body) +
        '</p><div class="lm-setup-picked" aria-hidden="true">' +
        normalizedInterests([...this.selected])
          .map((v) => "<span>" + icon(v) + "</span>")
          .join("") +
        '</div><label class="lm-setup-email-label" for="' +
        this.id +
        '-email">' +
        e(c.email_label) +
        "</label>" +
        '<input class="lm-setup-input" id="' +
        this.id +
        '-email" type="email" name="email" autocomplete="email" inputmode="email" autocapitalize="off" spellcheck="false" required maxlength="254" aria-describedby="' +
        this.id +
        "-error " +
        this.id +
        '-consent" value="' +
        e(this.email) +
        '">' +
        error +
        '</div><div class="lm-setup-footer"><button class="lm-setup-primary" type="submit" data-lm-submit>' +
        e(c.submit) +
        '<span aria-hidden="true">→</span></button><div class="lm-setup-reassurance"><p class="lm-setup-consent" id="' +
        this.id + '-consent">' + e(c.consent) + '</p><a class="lm-setup-privacy" href="' +
        e(this.config.privacyUrl || "/policies/privacy-policy") +
        '" target="_blank" rel="noopener">' +
        e(c.privacy) +
        "</a></div></div></form>";
    } else {
      this.stage.innerHTML =
        '<div class="lm-setup-form"><div class="lm-setup-main lm-setup-success"><div class="lm-setup-success-icon" aria-hidden="true"><svg viewBox="0 0 48 48"><rect x="6" y="12" width="36" height="26" rx="3"/><path d="m7 14 17 13 17-13"/></svg></div>' +
        heading(c.success_title) +
        '<p class="lm-setup-body">' +
        e(c.success_body) +
        '</p></div><div class="lm-setup-footer"><button type="button" class="lm-setup-primary" data-lm-browse>' +
        e(c.browse) +
        '<span aria-hidden="true">→</span></button></div></div>';
    }
    this.syncBusy();
  }
  syncBusy() {
    this.dialog.setAttribute("aria-busy", String(this.busy));
    const button = this.stage.querySelector("[data-lm-submit]");
    if (button) {
      button.disabled = this.busy;
      button.innerHTML =
        escapeHtml(this.busy ? this.copy().pending : this.copy().submit) +
        '<span aria-hidden="true">' +
        (this.busy ? "◌" : "→") +
        "</span>";
    }
    const email = this.stage.querySelector('input[name="email"]');
    if (email) email.disabled = this.busy;
    this.root.querySelector(".lm-setup-back").disabled = this.busy;
  }
  go(step, direction = 1) {
    this.step = step;
    this.render();
    if (!this.reducedMotion()) {
      this.animation?.cancel();
      this.animation = this.stage.animate(
        [
          { opacity: 0, transform: "translateX(" + direction * 10 + "px)" },
          { opacity: 1, transform: "translateX(0)" },
        ],
        { duration: 200, easing: "cubic-bezier(.2,.7,.2,1)" },
      );
    }
    this.stage.querySelector("h2").focus({ preventScroll: true });
  }
  showError(message) {
    const el = this.stage.querySelector(".lm-setup-error");
    if (el) el.textContent = message;
  }
  async submit() {
    const input = this.stage.querySelector('input[name="email"]');
    this.email = input.value.trim();
    input.value = this.email;
    if (
      !input.checkValidity() ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email)
    ) {
      input.setAttribute("aria-invalid", "true");
      this.showError(this.copy().email_error);
      input.focus();
      return;
    }
    const interests = normalizedInterests([...this.selected]);
    const signature = JSON.stringify([this.email, this.lang, interests]);
    if (!this.pendingAttempt || this.pendingAttempt.signature !== signature) {
      this.pendingAttempt = {
        signature,
        requestId: crypto.randomUUID(),
        payload: subscriptionPayload({
          email: this.config.preview ? "setup-preview@example.com" : this.email,
          interests,
          storefrontLocale: this.lang,
          listId: this.config.preview
            ? "LOCAL_REVIEW_ONLY"
            : this.config.listId,
        }),
      };
    }
    this.busy = true;
    this.showError("");
    this.syncBusy();
    try {
      this.result = await this.onSubmit(this.pendingAttempt);
      if (this.destroyed) return;
      this.busy = false;
      if (this.dialog.open) this.go(3);
      else {
        this.step = 3;
        this.render();
      }
    } catch (error) {
      if (this.destroyed) return;
      this.busy = false;
      this.syncBusy();
      this.showError(this.copy().submit_error);
    }
  }
  open() {
    if (this.dialog.open || this.closing) return false;
    this.previousFocus = document.activeElement;
    this.fitViewport();
    this.loadImages();
    this.dialog.showModal();
    this.stage.querySelector("h2").focus({ preventScroll: true });
    if (!this.reducedMotion()) {
      this.dialog.animate(
        [
          { opacity: 0, transform: "translateY(8px) scale(.985)" },
          { opacity: 1, transform: "none" },
        ],
        { duration: 200, easing: "cubic-bezier(.2,.7,.2,1)" },
      );
    }
    return true;
  }
  close({ restoreFocus = true, immediate = false } = {}) {
    if (!this.dialog.open || this.closing) return;
    this.closing = true;
    const finish = () => {
      this.dialog.close();
      if (
        !this.destroyed &&
        restoreFocus &&
        this.previousFocus?.isConnected &&
        !this.previousFocus.closest("[hidden]")
      )
        this.previousFocus.focus({ preventScroll: true });
      this.closing = false;
    };
    if (immediate || this.reducedMotion()) return finish();
    this.dialog
      .animate([{ opacity: 1 }, { opacity: 0 }], { duration: 150 })
      .finished.then(finish, finish);
  }
  destroy() {
    this.destroyed = true;
    this.signalController.abort();
    this.animation?.cancel();
    if (this.dialog.open) {
      this.dialog.close();
    }
    this.root.replaceChildren();
  }
}

function visible(element) {
  if (
    !element ||
    element.closest("[hidden]") ||
    !element.getClientRects().length
  )
    return false;
  if (
    ![...element.getClientRects()].some(
      (r) =>
        r.bottom > 0 &&
        r.right > 0 &&
        r.top < innerHeight &&
        r.left < innerWidth,
    )
  )
    return false;
  const style = getComputedStyle(element);
  return (
    style.visibility !== "hidden" &&
    style.display !== "none" &&
    style.opacity !== "0"
  );
}
function permission(kind) {
  try {
    const api = window.Shopify?.customerPrivacy;
    return (
      (kind === "analytics"
        ? api?.analyticsProcessingAllowed()
        : api?.preferencesProcessingAllowed()) === true
    );
  } catch {
    return false;
  }
}
function imageWarmupPolicy(policy) {
  const activeSeconds = Number.isInteger(policy?.activeSeconds)
    ? policy.activeSeconds
    : 7;
  return { ...policy, activeSeconds: Math.max(2, activeSeconds - 2) };
}
function imageWarmupAllowed(
  context,
  { policy, policyReady = false, autoEnabled = false, designMode = false } = {},
) {
  return policyReady && designMode !== true && autoEnabled &&
    !suppression(context, { policy: imageWarmupPolicy(policy) });
}
// Shopify's currentVisitorConsent() reports the visitor's explicit decision:
// analytics is '' when undecided, 'yes' when granted and 'no' when declined.
// Aggregate mode deliberately accepts only the first two values and layers
// browser privacy signals on top. It never writes consent or touches GA4.
function aggregatePermission(
  shopify = globalThis.Shopify,
  navigatorObject = globalThis.navigator,
) {
  try {
    if (navigatorObject?.globalPrivacyControl === true || navigatorObject?.doNotTrack === "1")
      return false;
    const api = shopify?.customerPrivacy;
    if (typeof api?.currentVisitorConsent !== "function") return false;
    const consent = api.currentVisitorConsent();
    if (!consent || typeof consent !== "object") return false;
    return consent.analytics === "" || consent.analytics === "yes";
  } catch {
    return false;
  }
}
function explicitAnalyticsPermission(shopify = globalThis.Shopify) {
  try {
    const api = shopify?.customerPrivacy;
    if (typeof api?.currentVisitorConsent !== "function") return false;
    const consent = api.currentVisitorConsent();
    return !!consent && typeof consent === "object" && consent.analytics === "yes";
  } catch {
    return false;
  }
}
function isThemePreview(config = {}, shopify = globalThis.Shopify || {}) {
  const themeRole = shopify.theme?.role;
  return config.preview !== false ||
    config.designMode === true ||
    shopify.previewMode === true ||
    (themeRole && themeRole !== "main");
}
function mountPopup(root, config, harness = {}) {
  const shopify = window.Shopify || {};
  const preview = isThemePreview(config, shopify);
  config = { ...config, preview };
  const now = harness.now || Date.now;
  let policy = triggerPolicy(config.triggerPolicy), policyReady = preview || config.designMode === true;
  // Public behaviour only: no identity, answers, cookies or page referrer.
  if (!policyReady) fetch("https://labomono.vercel.app/api/setup-popup-policy", {
    credentials: "omit", referrerPolicy: "no-referrer", signal: AbortSignal.timeout(1500),
  }).then(r => r.ok ? r.json() : null).then(value => { policy = triggerPolicy(value); })
    .catch(() => {}).finally(() => { policyReady = true; });
  const abort = new AbortController(),
    signal = abort.signal;
  let storage;
  try {
    if (preview || permission("preferences")) storage = localStorage;
  } catch {}
  const store = new StateStore({
    storage,
    key: preview ? "lm_setup_review_v2" : "lm_setup_v1",
    now,
    holdoutPercent: config.holdoutPercent || 0,
  });
  let sessionStorage;
  try {
    sessionStorage = window.sessionStorage;
  } catch {}
  const frequencyCap = new SessionFrequencyCap({
    storage: sessionStorage,
    key: frequencyCapKey(preview),
  });
  const service = preview
    ? new PreviewService()
    : new KlaviyoSubscription(config);
  let pathname = location.pathname,
    pageAt = now(),
    quietAt = now(),
    overlayAt = -Infinity;
  let hadOverlay = false,
    invitedJourney = false,
    pointerDown = false,
    opening = false,
    destroyed = false,
    lastTick = now();
  let cartKnown = Number.isInteger(config.cartCount),
    cartCount = config.cartCount || 0;
  let cartPromise = null,
    cartCheckedAt = -Infinity,
    consentRefreshDone = !!storage;
  let ui,
    progressOpening = "manual",
    registeredSession,
    lastActivityRecorded = 0;
  const measurement = new PopupMeasurement({
    context: () => ({
      locale: ui?.lang || config.locale,
      group: harness.context?.().group || store.read().session.group,
      device: matchMedia("(max-width: 699px)").matches ? "mobile" : "desktop",
    }),
    consent: () => permission("analytics"),
    preview,
    measurementId: config.measurementId,
    observe: harness.onEvent,
    send: (name, payload) => {
      window.dataLayer = window.dataLayer || [];
      function gtag() {
        window.dataLayer.push(arguments);
      }
      gtag("event", name, payload);
    },
  });
  const progressMode = ["consented", "aggregate-v2"].includes(config.interestMeasurementMode)
    ? config.interestMeasurementMode
    : "off";
  const progress = new PopupProgress({
    enabled: config.interestMeasurementEnabled === true && progressMode !== "off",
    mode: progressMode,
    preview,
    consent: () => progressMode === "aggregate-v2"
      ? aggregatePermission()
      : explicitAnalyticsPermission(),
    context: () => ({
      opening: progressOpening,
      locale: ui?.lang || config.locale,
      device: matchMedia("(max-width: 699px)").matches ? "mobile" : "desktop",
    }),
    observe: harness.onProgress,
  });
  const event = (name, details = {}) => measurement.event(name, details);
  const trackEligibility = () => {
    if (store.read().session.eligibleTracked) return;
    const sent = event("eligible");
    if (sent || preview)
      store.update((s) => {
        s.session.eligibleTracked = true;
      });
  };
  const interrupt = (reason) => {
    if (!ui?.dialog.open) return;
    event("interrupted", { step: ui.step, interruption_reason: reason });
    progress.close();
    ui.close({ immediate: true, restoreFocus: false });
  };
  const markPurchase = () => {
    store.update((s) => {
      s.session.purchaseIntent = true;
      s.session.lastActivity = now();
    });
    if (!invitedJourney) interrupt("shopping");
  };
  function readContext() {
    const state = store.read(),
      session = state.session;
    const anyVisible = (selector) =>
      [...document.querySelectorAll(selector)].some(
        (el) => !root.contains(el) && visible(el),
      );
    const pendingConsent = anyVisible("[data-cali-consent-bar]");
    const consentDialog = anyVisible("[data-cali-consent-sheet], #shopify-pc__prefs");
    const otherConsent = anyVisible(".shopify-pc__banner__dialog");
    const consentVisible = pendingConsent || consentDialog || otherConsent;
    const onlyPendingConsent = pendingConsent && !consentDialog && !otherConsent;
    const cartOpen = anyVisible(".drawer--is-open");
    const otherForm = anyVisible(
      '[data-testid="klaviyo-form"], .klaviyo-form [role="dialog"]',
    );
    const otherModal = [...document.querySelectorAll(
      'dialog[open], [role="dialog"], [aria-modal="true"], .modal--is-active, .predictive-search--is-active, .site-nav__dropdown',
    )].some(el => !root.contains(el) && !el.matches('[data-cali-consent-bar]') && visible(el));
    const active = document.activeElement;
    const editing =
      !root.contains(active) &&
      !!active?.matches(
        'input:not([type="button"]):not([type="submit"]), textarea, select, [contenteditable="true"]',
      );
    const context = {
      pathname,
      builderPage: !!document.querySelector("#react-customization-app"),
      subscriber: config.subscriber === true,
      submitted: state.submitted,
      cartKnown,
      cartCount,
      purchaseIntent: session.purchaseIntent,
      consentVisible,
      consentDialog,
      onlyPendingConsent,
      cartOpen,
      otherForm,
      otherModal,
      pageVisible: document.visibilityState === "visible",
      editing,
      pointerDown,
      storageAvailable: store.available,
      frequencyCapAvailable: frequencyCap.available,
      frequencyCapShown: frequencyCap.shown,
      dismissedUntil: state.dismissedUntil,
      shownThisSession: session.shown,
      group: session.group,
      now: now(),
      activeSeconds: session.activeSeconds,
      pageCount: session.pages.length,
      scrollFraction:
        document.documentElement.scrollHeight > innerHeight
          ? scrollY / (document.documentElement.scrollHeight - innerHeight)
          : 0,
      pageSeconds: (now() - pageAt) / 1000,
      quietSeconds: (now() - quietAt) / 1000,
      overlaySeconds: (now() - overlayAt) / 1000,
    };
    return { ...context, ...harness.context?.() };
  }
  const registerPage = () => {
    const c = readContext();
    if (
      /^\/(?:cart|checkout|checkouts)(?:\/|$)/.test(unlocalizedPath(c.pathname))
    )
      markPurchase();
    if (!protectedPage(c.pathname, c.builderPage)) {
      const page = unlocalizedPath(c.pathname);
      store.update((s) => {
        if (!s.session.pages.includes(page)) s.session.pages.push(page);
      });
    }
  };
  async function refreshCart(force = false) {
    if (preview) return;
    if (cartPromise) return cartPromise;
    if (!force && now() - cartCheckedAt < 10000) return;
    cartKnown = false;
    const routeRoot = window.Shopify?.routes?.root || config.routeRoot || "/";
    cartPromise = (async () => {
      try {
        const url = new URL(routeRoot + "cart.js", location.origin);
        if (url.origin !== location.origin)
          throw new Error("Cart origin mismatch");
        const response = await fetch(url, {
          credentials: "same-origin",
          cache: "no-store",
          signal: AbortSignal.timeout(5000),
        });
        if (!response.ok) throw new Error("Cart unavailable");
        const cart = await response.json();
        if (!Number.isInteger(cart.item_count) || cart.item_count < 0)
          throw new Error("Invalid cart");
        if (destroyed) return;
        cartKnown = true;
        cartCount = cart.item_count;
        if (cartCount > 0) markPurchase();
      } catch {
        cartKnown = false;
      } finally {
        cartPromise = null;
        cartCheckedAt = now();
      }
    })();
    return cartPromise;
  }
  function dismiss(userDismissed) {
    if (userDismissed && !store.read().submitted) {
      store.update((s) => {
        s.dismissedUntil = now() + POLICY.cooldownDays * 86400000;
      });
      event("dismissed", { step: ui.step });
    }
    progress.close();
    ui.close();
    sync();
  }
  async function requestOpen(manual = false, invited = false) {
    if (opening || ui.dialog.open || ui.closing || ui.busy || destroyed) return false;
    let reason = suppression(readContext(), { manual, invited, policy });
    if (reason) {
      harness.onStatus?.(reason);
      return false;
    }
    opening = true;
    if (!preview && !invited) await refreshCart(true);
    reason = suppression(readContext(), { manual, invited, policy });
    if (reason || destroyed) {
      opening = false;
      harness.onStatus?.(reason || "destroyed");
      return false;
    }
    invitedJourney = manual && invited;
    if (invitedJourney) {
      // Preserve in-memory choices for editing, without clearing the durable
      // completion flag or making any request until the form is submitted.
      ui.result = null;
      ui.pendingAttempt = null;
      ui.go(1);
    }
    if (!manual) trackEligibility();
    if (ui.open()) {
      frequencyCap.markShown();
      store.update((s) => {
        s.session.shown = true;
        s.session.lastActivity = now();
      });
      progressOpening = manual ? "manual" : "automatic";
      measurement.opened(progressOpening, ui.step);
      progress.open(progressOpening, [...ui.selected]);
    }
    opening = false;
    sync();
    return true;
  }
  ui = new PopupUI(root, config, {
    onDismiss: dismiss,
    onOpenRequest: () => requestOpen(true),
    onInterestChange: (interests) => progress.selection(interests),
    onContinue: (interests) => progress.continue(interests),
    onEvent: event,
    onSubmit: async (attempt) => {
      const progressAttemptId = progress.submitted([...ui.selected]);
      const capture = measurement.submission(attempt, ui.selected.size);
      const options = harness.submission?.() || {};
      let result;
      try {
        result = await (preview
          ? new Promise((resolve) => setTimeout(resolve, 450)).then(() =>
              service.submit(attempt.payload, {
                ...options,
                requestId: attempt.requestId,
              }),
            )
          : service.submit(attempt.payload));
        if (!result.accepted) throw new Error("Unaccepted signup");
      } catch (error) {
        measurement.event(
          "error",
          { ...capture.details, error_kind: error.kind || "unavailable" },
          capture.journey,
        );
        throw error;
      }
      store.update((s) => {
        s.submitted = true;
      });
      measurement.event(
        "accepted",
        {
          ...capture.details,
          confirmation_required: result.confirmationRequired,
        },
        capture.journey,
      );
      progress.accepted(progressAttemptId);
      harness.onReceipt?.(attempt.payload, result);
      sync();
      return result;
    },
  });
  function sync() {
    if (destroyed) return;
    const c = readContext();
    const overlay = blockingOverlay(c);
    for (const invitation of document.querySelectorAll('[data-lm-setup-footer]')) {
      if (invitation.hidden) invitation.hidden = false;
      const fallback = invitation.parentElement.querySelector('[data-lm-setup-footer-fallback]');
      if (fallback && !fallback.hidden) fallback.hidden = true;
      invitation.querySelector('[data-lm-setup-open]').disabled = overlay || ui.busy;
    }
    if (overlay || (hadOverlay && !overlay)) overlayAt = now();
    hadOverlay = !!overlay;
    const reason = suppression(
      { ...c, pointerDown: false, editing: false },
      { manual: true },
    );
    if (ui.invite) ui.invite.parentElement.hidden = !!reason;
    if (
      ui.dialog.open &&
      (overlay || (!invitedJourney &&
        (protectedPage(c.pathname, c.builderPage) ||
          c.purchaseIntent || c.cartCount > 0)))
    )
      interrupt(
        overlay
          ? "overlay"
          : c.purchaseIntent || c.cartCount > 0
            ? "shopping"
            : "protected_page",
      );
    harness.onStatus?.(suppression(readContext(), { policy }) || "eligible");
  }
  const activity = () => {
    quietAt = now();
    if (now() - lastActivityRecorded > 1000) {
      store.touch();
      lastActivityRecorded = now();
    }
  };
  const listen = (target, name, fn, options = {}) =>
    target.addEventListener(name, fn, { ...options, signal });
  listen(
    document,
    "pointerdown",
    () => {
      pointerDown = true;
      activity();
    },
    { passive: true },
  );
  listen(
    document,
    "pointerup",
    () => {
      pointerDown = false;
      activity();
    },
    { passive: true },
  );
  listen(
    document,
    "pointercancel",
    () => {
      pointerDown = false;
      activity();
    },
    { passive: true },
  );
  listen(document, "keydown", activity, { passive: true });
  listen(document, "input", activity, { passive: true });
  listen(window, "scroll", activity, { passive: true });
  listen(window, "blur", () => {
    pointerDown = false;
  });
  listen(document, "click", (e) => {
    const invitation = e.target.closest('[data-lm-setup-open="footer"]');
    if (invitation) {
      e.preventDefault();
      requestOpen(true, true);
      return;
    }
    const anchor = e.target.closest("a[href]");
    if (anchor) {
      const url = new URL(anchor.href, location.href);
      if (
        url.origin === location.origin &&
        /^\/(?:cart|checkout)(?:\/|$)/.test(unlocalizedPath(url.pathname))
      )
        markPurchase();
      if (anchor.matches("[data-lm-setup-open]")) {
        e.preventDefault();
        requestOpen(true);
      }
    }
  });
  listen(document, "ajaxProduct:added", () => {
    markPurchase();
    refreshCart(true);
    sync();
  });
  listen(document, "cart:updated", (e) => {
    const count = e.detail?.cart?.item_count;
    if (Number.isInteger(count) && count >= 0) {
      cartCount = count;
      cartKnown = true;
      cartCheckedAt = now();
      if (count) markPurchase();
    } else refreshCart(true);
    sync();
  });
  [
    "drawerOpen",
    "modalOpen",
    "predictive-search:open",
    "quickview:open",
  ].forEach((name) => listen(document, name, sync));
  listen(window, "storage", (e) => {
    if (e.key !== store.key) return;
    // Another tab's session update must not dismiss this tab's own receipt.
    // Incomplete forms still close when signup has completed elsewhere.
    if (store.read().submitted && !ui.busy && !ui.result?.accepted && !invitedJourney) ui.close();
    sync();
  });
  const refreshConsent = () => {
    if (preview) return;
    if (permission("preferences") && !consentRefreshDone) {
      try {
        store.storage = localStorage;
        store.available = true;
        store.update((s) => {
          s.submitted ||= store.memory.submitted === true;
          s.dismissedUntil = Math.max(
            s.dismissedUntil,
            store.memory.dismissedUntil || 0,
          );
          s.session.shown ||= store.memory.session?.shown === true;
          s.session.purchaseIntent ||=
            store.memory.session?.purchaseIntent === true;
        });
        consentRefreshDone = true;
        store.touch();
        registerPage();
      } catch {
        store.available = false;
      }
    } else if (!permission("preferences")) {
      store.memory = store.read();
      store.available = false;
      consentRefreshDone = false;
    }
  };
  listen(document, "visitorConsentCollected", () => {
    refreshConsent();
    progress.consentChanged();
    overlayAt = now();
    sync();
  });
  listen(window, "pageshow", () => {
    pageAt = quietAt = now();
    pointerDown = false;
    refreshCart(true);
    sync();
  });
  const observer = new MutationObserver((records) => {
    if (records.some((r) => !root.contains(r.target))) sync();
  });
  observer.observe(document.body, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ["class", "hidden", "open", "aria-expanded"],
  });
  const languageObserver = new MutationObserver(() =>
    ui.setLocale(document.documentElement.lang),
  );
  languageObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["lang"],
  });
  store.touch();
  registerPage();
  registeredSession = store.read().session.startedAt;
  if (cartCount > 0) markPurchase();
  if (!preview) refreshCart(true);
  sync();
  const timer = setInterval(() => {
    if (!root.isConnected) return destroy();
    refreshConsent();
    if (registeredSession !== store.read().session.startedAt) {
      store.touch();
      registerPage();
      registeredSession = store.read().session.startedAt;
    }
    if (!preview && !cartKnown) refreshCart();
    if (pathname !== location.pathname) {
      pathname = location.pathname;
      pageAt = quietAt = now();
      registerPage();
      refreshCart(true);
    }
    const c = readContext(),
      elapsed = Math.min(Math.max((now() - lastTick) / 1000, 0), 1.5);
    lastTick = now();
    if (
      !ui.dialog.open &&
      !suppression({ ...c, pointerDown: false }, { manual: true }) &&
      !c.pointerDown &&
      !c.editing
    ) {
      store.update((s) => {
        s.session.activeSeconds += elapsed;
      });
    }
    sync();
    const autoEnabled = harness.autoEnabled?.() ?? !config.designMode;
    const eligibilityContext = readContext();
    const eligible = policyReady && !suppression(eligibilityContext, { ignoreHoldout: true, policy });
    const warmEligible = imageWarmupAllowed(eligibilityContext, {
      policy,
      policyReady,
      autoEnabled,
      designMode: config.designMode,
    });
    if (warmEligible && !ui.dialog.open) ui.prepareImages();
    if (eligible) trackEligibility();
    if (eligible && autoEnabled)
      requestOpen(false);
  }, 500);
  function destroy() {
    if (destroyed) return;
    destroyed = true;
    clearInterval(timer);
    abort.abort();
    observer.disconnect();
    languageObserver.disconnect();
    for (const invitation of document.querySelectorAll('[data-lm-setup-footer]')) {
      invitation.hidden = true;
      const fallback = invitation.parentElement.querySelector('[data-lm-setup-footer-fallback]');
      if (fallback) fallback.hidden = false;
    }
    ui.destroy();
  }
  return {
    ui,
    store,
    service,
    open: requestOpen,
    sync,
    context: readContext,
    destroy,
  };
}

const previous = window.LaboMonoSetupPopup;
if (previous?.init) {
  previous.init();
} else {
  const instances = new Map();
  function init() {
    for (const [root, controller] of instances) {
      if (!root.isConnected) {
        controller.destroy();
        instances.delete(root);
      }
    }
    document.querySelectorAll("[data-lm-setup-root]").forEach((root) => {
      if (instances.has(root)) return;
      const configNode = root.querySelector("[data-lm-setup-config]");
      if (!configNode) return;
      try {
        const config = JSON.parse(configNode.textContent);
        instances.set(root, mountPopup(root, config));
      } catch {
        root.hidden = true;
      }
    });
  }
  window.LaboMonoSetupPopup = {
    init,
    open: () => [...instances.values()][0]?.open(true),
  };
  document.addEventListener("shopify:section:load", init);
  document.addEventListener("shopify:section:unload", (event) => {
    for (const [root, controller] of instances) {
      if (event.target.contains(root)) {
        controller.destroy();
        instances.delete(root);
      }
    }
  });
  init();
}

})();
